<template>
  <div class="calculators">
    <div v-if="abTest || showPromoBanner" class="container banner">
      <PromoBanner v-if="showPromoBanner" :is-campaign="showPromoBanner" />
      <ABBanner
        v-else-if="abTest && $config.platform === 'ds'"
        first="GET"
        second="World-Class"
        title="Data Science"
        third="Courses"
        long-banner
      />
    </div>

    <div class="container">
      <Calculators />
      <Articles />
      <FAQ v-if="$store.state.calculators.calculator !== 'variance'" />
    </div>
    <div class="up-arrow-wrapper">
      <a
        class="up-arrow-btn"
        :class="{ 'visible': scrolled }"
        @click="scrollToTop"
      >
        <UpArrow />Top
      </a>
    </div>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import ABBanner from '@itbpbg/lms-components/src/ABBanner';
import PromoBanner from '@itbpbg/lms-components/src/PromoBanner';
import Calculators from '../../components/Calculators/Content/Calculators';
import Articles from '../../components/Calculators/Content/Articles';
import FAQ from '../../components/Calculators/Content/FAQ';
import UpArrow from '../../assets/svg/icon-arrow-blog-up.svg?vue-component';

export default {
  components: {
    Calculators,
    Articles,
    UpArrow,
    FAQ,
    ABBanner,
    PromoBanner,
  },
  mixins: [global],
  middleware({ $config, error, route }) {
    const validPaths = [
      '/calculators/confidence-interval-calculator/',
      '/calculators/mean-median-mode-calculator/',
      '/calculators/skewness-calculator/',
      '/calculators/kurtosis-calculator/',
      '/calculators/variance-calculator/',
      '/calculators/standard-deviation-calculator/',
      '/calculators/coefficient-of-variation-calculator/',
      '/calculators/linear-regression-calculator/',
      '/calculators/p-value-calculator/',
      '/calculators/hypothesis-test-calculator/',
      '/calculators/difference-in-means-calculator/',
    ];

    if ($config.platform === 'fa' || !validPaths.includes(route.path)) {
      return error({ statusCode: 404, message: 'Page not found' });
    }
    return true;
  },
  async asyncData({
    $axios, route, store,
  }) {
    const tests = await $axios.$get(`/pages/tests?ab-test=${Date.now()}&route-name=${route.name}`).catch(() => false);

    if (Object.keys(tests || {}).length) {
      store.dispatch('tests/addUserTests', tests);
    }
  },
  data() {
    return {
      scrolled: false,
      metaTitles: {
        'confidence-interval-calculator': 'Confidence Interval Calculator',
        'mean-median-mode-calculator': 'Mean, Median, Mode Calculator',
        'skewness-calculator': 'Skewness Calculator',
        'kurtosis-calculator': 'Kurtosis Calculator',
        'variance-calculator': 'Variance Calculator',
        'standard-deviation-calculator': 'Standard Deviation Calculator',
        'coefficient-of-variation-calculator': 'Coefficient of Variation Calculator',
        'linear-regression-calculator': 'Linear Regression Calculator',
        'p-value-calculator': 'Quick P-Value Calculator',
        'hypothesis-test-calculator': 'Hypothesis Test Calculator',
        'difference-in-means-calculator': 'Difference in Means Calculator',
      },
    };
  },
  head() {
    let metaDescription;

    if (this.metaTitles[this.$route.path.split('/')[2]] === 'Linear Regression Calculator') {
      metaDescription = 'Perform linear regression analysis quickly with our calculator. Get the equation, step-by-step calculations, ANOVA table, Python and R codes, etc.';
    } else if (this.metaTitles[this.$route.path.split('/')[2]] === 'Quick P-Value Calculator') {
      metaDescription = 'Calculate p-values from a z-score, t-score, F-statistic, or chi-square in one click. Get the step-by-step calculations and quick results with Python and R code.';
    } else if (this.metaTitles[this.$route.path.split('/')[2]] === 'Hypothesis Test Calculator') {
      metaDescription = 'Use this Hypothesis Test Calculator for quick results in Python and R. Learn the step-by-step hypothesis test process and why hypothesis testing is important.';
    } else if (this.metaTitles[this.$route.path.split('/')[2]] === 'Difference in Means Calculator') {
      metaDescription = 'Want to run a quick hypothesis test to compare means? Calculate t-test, z-test, and p-value in Python and R code. Try out the Difference in Means Calculator now.';
    } else {
      metaDescription = `Do you need to find a ${this.metaTitles[this.$route.path.split('/')[2]]} quickly? Input your data to obtain the metric, step-by-step calculation, Python and R codes, ${this.$route.path.split('/')[2] === 'mean-median-mode-calculator' ? 'etc' : 'and more'}. Calculate now.`;
    }

    return {
      titleTemplate: `${this.metaTitles[this.$route.path.split('/')[2]]} | 365 Data Science`,
      meta: this.metaProps({ metaDescription }),
      script: [
        { src: 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.7/MathJax.js?config=TeX-MML-AM_SVG', async: true },
      ],
    };
  },
  computed: {
    abTest() {
      // return this.$store.state.tests?.tests?.['calculators-banner'] === 1;
      return true;
    },
    showPromoBanner() {
      const isCampaign = ['most-wanted-2024', 'future-proof-2024', 'fast-track-2024'].includes(this.$store.state.campaign);
      return isCampaign && this.$store.getters['banners/showPromoBannerToUser'];
    },
  },
  created() {
    this.handleScroll();
  },
  methods: {
    scrollToTop() {
      if (process.client) {
        window.scroll({ top: 0, left: 0, behavior: 'smooth' });
      }
    },
    handleScroll() {
      if (process.client) {
        window.onscroll = () => {
          this.scrolled = window.scrollY > 0 && this.$store.state.layout === 'desktop';
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.calculators {
  padding-top: var(--spacing-160);
  background: var(--main-light-shade-1);

  .container {
    &:not(.banner) {
      padding: 0;
    }

    &.banner {
      margin-bottom: var(--spacing-80);

      ::v-deep .side .banner-title {
        .first {
          font-size: 18px;
          font-weight: 500;
          line-height: 110%;
        }

        .second {
          font-size: 21px;
          font-weight: 600;
          line-height: 85%;
        }

        .title {
          font-size: 40px;
          font-weight: 700;
          line-height: 83%;
          letter-spacing: -0.391px;
        }

        .third {
          font-size: 28px;
          font-weight: 600;
          line-height: 82%;
        }
      }
    }
  }

  .up-arrow-wrapper {
    width: 56px;
    margin: 0 125px 0 auto;
    padding-bottom: var(--spacing-60);
    position: sticky;
    bottom: -6px;
    transition: all var(--duration) ease-in;

    .up-arrow-btn {
      width: 56px;
      height: 56px;
      background: var(--main-dark-shade-1);
      border-radius: 3px;
      color: var(--main-light);
      padding: var(--spacing-12);
      text-align: center;
      opacity: 0;
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;

      &.visible {
        opacity: 1;
      }

      svg {
        margin-bottom: 5px;

        path[stroke] {
          stroke: var(--main-light);
        }
      }

      &:hover {
        color: var(--main-light);
      }
    }
  }
}

@include bp($bp-mobile) {
  .calculators {
    overflow: hidden;
    padding-top: 136px;

    .container ::v-deep .row {
      margin-left: 0;
      margin-right: 0;

      .col {
        padding: 0;
      }
    }

    .up-arrow-wrapper {
      display: none;
    }
  }
}
</style>
